"use client";

import {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import Script from "next/script";

// ── Turnstile JS API types ──────────────────────────────────────
interface TurnstileAPI {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      "expired-callback"?: () => void;
      "error-callback"?: () => void;
      size?: "normal" | "compact" | "flexible";
      appearance?: "always" | "execute" | "interaction-only";
      action?: string;
      cData?: string;
    }
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileAPI;
  }
}

// ── Context ─────────────────────────────────────────────────────
interface TurnstileContextValue {
  /** The current Turnstile token, or null if not yet available. */
  token: string | null;
  /**
   * Reset the widget to obtain a fresh token.
   * Useful after consuming a token (they're single-use).
   */
  resetToken: () => void;
  /**
   * Show a visible interactive Turnstile challenge and return a fresh token.
   * Use this as a fallback when the invisible widget's token is rejected.
   * The returned promise resolves with the token, or rejects if the user
   * cancels or the challenge fails.
   */
  requestInteractiveToken: () => Promise<string>;
  /**
   * A token for one request: the current one, else the next one the off-screen
   * widget produces (it re-solves after every `resetToken`), else the visible
   * challenge after `TOKEN_WAIT_MS`. `null` when none can be had: no site key,
   * the script blocked, the challenge cancelled or failed.
   */
  acquireToken: () => Promise<string | null>;
}

/** How long `acquireToken` waits for the off-screen widget before asking the user. */
const TOKEN_WAIT_MS = 8000;

const TurnstileContext = createContext<TurnstileContextValue>({
  token: null,
  resetToken: () => {},
  requestInteractiveToken: () => Promise.reject(new Error("Turnstile not available")),
  acquireToken: () => Promise.resolve(null),
});

/**
 * Hook to access the global Turnstile token for per-action forms.
 * `/api/chat` is gated by Cloudflare Pre-Clearance instead — see ARCH-018.
 */
export function useTurnstile(): TurnstileContextValue {
  return useContext(TurnstileContext);
}

// ── Provider ────────────────────────────────────────────────────
interface TurnstileProviderProps {
  children: ReactNode;
}

/**
 * Global Turnstile provider — loads the Cloudflare Turnstile script once
 * and maintains an invisible widget that auto-generates and refreshes tokens.
 *
 * Mount this near the root of the app (e.g. in Providers.tsx).
 * Any component can then call `useTurnstile()` to get the current token.
 *
 * Requires `NEXT_PUBLIC_TURNSTILE_SITE_KEY` to be set.
 * If the key is missing (e.g. local dev without it), the provider still
 * renders children — `token` will remain `null`.
 */
export function TurnstileProvider({ children }: TurnstileProviderProps) {
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  const [token, setToken] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  // `token` as of now, for `acquireToken` callers holding an older render's value.
  const tokenRef = useRef<string | null>(null);
  const waitersRef = useRef(new Set<(t: string) => void>());

  const renderWidget = useCallback(() => {
    if (!siteKey || !containerRef.current || widgetIdRef.current) return;
    if (!window.turnstile) return;

    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: siteKey,
      callback: (t: string) => {
        tokenRef.current = t;
        setToken(t);
        for (const waiter of waitersRef.current) waiter(t);
        waitersRef.current.clear();
      },
      "expired-callback": () => {
        tokenRef.current = null;
        setToken(null);
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
      },
      "error-callback": () => {
        console.warn("[Turnstile] Challenge error — will retry automatically");
      },
      size: "compact",
      appearance: "interaction-only",
      action: "okopunkte_site",
    });
  }, [siteKey]);

  const resetToken = useCallback(() => {
    tokenRef.current = null;
    setToken(null);
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, []);

  /**
   * Show a visible Turnstile challenge as a fallback when the invisible
   * widget token is rejected (false positive). Creates a centered overlay
   * with a normal-sized widget the user can interact with.
   */
  const requestInteractiveToken = useCallback((): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!siteKey || !window.turnstile) {
        reject(new Error("Turnstile not available"));
        return;
      }

      // Create overlay backdrop
      const overlay = document.createElement("div");
      overlay.setAttribute("role", "dialog");
      overlay.setAttribute("aria-modal", "true");
      overlay.setAttribute("aria-label", "Sicherheitsüberprüfung");
      Object.assign(overlay.style, {
        position: "fixed",
        inset: "0",
        zIndex: "99999",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.5)",
      });

      // Card container
      const card = document.createElement("div");
      Object.assign(card.style, {
        background: "white",
        borderRadius: "16px",
        padding: "24px",
        textAlign: "center",
        maxWidth: "340px",
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
      });

      const title = document.createElement("p");
      title.textContent = "Bitte bestätigen Sie kurz, dass Sie kein Roboter sind.";
      Object.assign(title.style, {
        margin: "0 0 16px",
        fontSize: "14px",
        lineHeight: "1.5",
        color: "#333",
      });

      const widgetContainer = document.createElement("div");
      Object.assign(widgetContainer.style, {
        display: "flex",
        justifyContent: "center",
        minHeight: "65px",
      });

      card.appendChild(title);
      card.appendChild(widgetContainer);
      overlay.appendChild(card);
      document.body.appendChild(overlay);

      let interactiveWidgetId: string | null = null;
      let settled = false;
      const showTime = Date.now();
      // Minimum time (ms) to keep the overlay visible so it doesn't flash
      // when Cloudflare auto-solves the challenge on localhost/low-risk.
      const MIN_DISPLAY_MS = 1200;

      const cleanup = () => {
        if (interactiveWidgetId && window.turnstile) {
          window.turnstile.remove(interactiveWidgetId);
        }
        if (document.body.contains(overlay)) {
          document.body.removeChild(overlay);
        }
      };

      /** Delay cleanup so the overlay doesn't flash */
      const scheduleCleanup = (fn: () => void) => {
        const elapsed = Date.now() - showTime;
        if (elapsed >= MIN_DISPLAY_MS) {
          fn();
        } else {
          setTimeout(fn, MIN_DISPLAY_MS - elapsed);
        }
      };

      // Close on backdrop click (not card click)
      overlay.addEventListener("click", (e) => {
        if (e.target === overlay && !settled) {
          settled = true;
          cleanup();
          reject(new Error("Verification cancelled"));
        }
      });

      // Close on Escape key
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape" && !settled) {
          settled = true;
          window.removeEventListener("keydown", handleKeyDown);
          cleanup();
          reject(new Error("Verification cancelled"));
        }
      };
      window.addEventListener("keydown", handleKeyDown);

      interactiveWidgetId = window.turnstile!.render(widgetContainer, {
        sitekey: siteKey,
        appearance: "always",
        size: "normal",
        callback: (freshToken: string) => {
          if (settled) return;
          settled = true;
          window.removeEventListener("keydown", handleKeyDown);
          // Show a brief success state before closing
          title.textContent = "Verifizierung erfolgreich.";
          scheduleCleanup(() => {
            cleanup();
            resolve(freshToken);
          });
        },
        "expired-callback": () => {
          if (settled) return;
          settled = true;
          window.removeEventListener("keydown", handleKeyDown);
          cleanup();
          reject(new Error("Verification expired"));
        },
        "error-callback": () => {
          if (settled) return;
          settled = true;
          window.removeEventListener("keydown", handleKeyDown);
          title.textContent = "Verifizierung fehlgeschlagen. Bitte versuchen Sie es erneut.";
          // Keep overlay visible a bit so user can read the message
          setTimeout(() => {
            cleanup();
            reject(new Error("Verification failed"));
          }, 2000);
        },
      });
    });
  }, [siteKey]);

  const acquireToken = useCallback(async (): Promise<string | null> => {
    const ready = await new Promise<string | null>((resolve) => {
      if (tokenRef.current) return resolve(tokenRef.current);
      if (!siteKey) return resolve(null);
      const waiter = (t: string) => {
        clearTimeout(timer);
        resolve(t);
      };
      const timer = setTimeout(() => {
        waitersRef.current.delete(waiter);
        resolve(null);
      }, TOKEN_WAIT_MS);
      waitersRef.current.add(waiter);
    });
    return ready ?? requestInteractiveToken().catch(() => null);
  }, [siteKey, requestInteractiveToken]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  return (
    <TurnstileContext.Provider value={{ token, resetToken, requestInteractiveToken, acquireToken }}>
      {/* Load Turnstile script once globally */}
      {siteKey && (
        <>
          <Script
            src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
            strategy="afterInteractive"
            onReady={renderWidget}
          />
          {/* Must NOT use display:none — Turnstile needs the container in layout */}
          <div
            ref={containerRef}
            aria-hidden="true"
            style={{ position: "fixed", left: "-9999px", top: "-9999px" }}
          />
        </>
      )}
      {children}
    </TurnstileContext.Provider>
  );
}
